import AuthPageGuard from "components/AuthPageGuard";
import {
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Input,
  Text,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { authService } from "services/authService";
import { getThemeColor } from "theme/colors";

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!token) {
    return (
      <AuthPageGuard>
        <Box w="full">
          <VStack gap={8} align="stretch">
            <Box
              p={4}
              borderRadius="lg"
              bg={getThemeColor("alert.error.bg")}
              border="1px solid"
              borderColor={getThemeColor("alert.error.border")}
              color={getThemeColor("alert.error.text")}
            >
              {t("app.auth.resetPassword.invalidToken")}
            </Box>
            <Flex justify="center">
              <Link to="/auth/forgot-password">
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  color={getThemeColor("text.link")}
                >
                  {t("app.auth.forgotPassword.title")}
                </Text>
              </Link>
            </Flex>
          </VStack>
        </Box>
      </AuthPageGuard>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmPassword) {
      setError(t("app.validation.match"));
      return;
    }
    if (newPassword.length < 8) {
      setError(t("app.validation.minLength", { min: 8 }));
      return;
    }

    setLoading(true);
    try {
      await authService.resetPassword(token, newPassword);
      navigate("/auth/sign-in", {
        state: { message: t("app.auth.resetPassword.successMessage") },
      });
    } catch {
      setError(t("app.auth.resetPassword.failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageGuard>
      <Box w="full">
        <VStack gap={8} align="stretch">
          <VStack gap={2} align="start">
            <Heading
              size="2xl"
              color={getThemeColor("text.primary")}
              fontWeight="bold"
            >
              {t("app.auth.resetPassword.title")}
            </Heading>
            <Text fontSize="md" color={getThemeColor("text.secondary")}>
              {t("app.auth.resetPassword.subtitle")}
            </Text>
          </VStack>

          <Card.Root>
            <Card.Body p={8}>
              <form onSubmit={handleSubmit}>
                <VStack gap={5} align="stretch">
                  {error && (
                    <Box
                      p={3}
                      borderRadius="md"
                      bg={getThemeColor("alert.error.bg")}
                      border="1px solid"
                      borderColor={getThemeColor("alert.error.border")}
                      color={getThemeColor("alert.error.text")}
                    >
                      <Text fontSize="sm" fontWeight="medium">
                        {error}
                      </Text>
                    </Box>
                  )}

                  <Input
                    type="password"
                    label={t("app.auth.resetPassword.newPasswordLabel")}
                    placeholder={t(
                      "app.auth.resetPassword.newPasswordPlaceholder",
                    )}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    size="lg"
                    required
                  />
                  <Input
                    type="password"
                    label={t("app.auth.resetPassword.confirmPasswordLabel")}
                    placeholder={t(
                      "app.auth.resetPassword.confirmPasswordPlaceholder",
                    )}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    size="lg"
                    required
                  />

                  <Button
                    type="submit"
                    disabled={loading}
                    variant="solid"
                    colorPalette="blue"
                    size="lg"
                    w="full"
                    mt={2}
                  >
                    {loading
                      ? t("app.auth.resetPassword.resetting")
                      : t("app.auth.resetPassword.submitButton")}
                  </Button>
                </VStack>
              </form>
            </Card.Body>
          </Card.Root>

          <Flex justify="center">
            <Link to="/auth/sign-in">
              <Text
                fontSize="sm"
                fontWeight="semibold"
                color={getThemeColor("text.link")}
              >
                {t("app.auth.resetPassword.backToSignIn")}
              </Text>
            </Link>
          </Flex>
        </VStack>
      </Box>
    </AuthPageGuard>
  );
}
